/* Getting started guide: a lump of soft clay that opens into a ring as you scroll through the
   four stages (soft clay → dry clay → fired → polished silver). Driven by the
   'atl-journey:progress' event from atl-journey.js. Falls back to a CSS drawing without WebGL.
   Everything is deliberately a little irregular, like a handmade piece: a lumpy ball, a band that
   varies in thickness and isn't perfectly round or flat, dents that sanding softens but doesn't
   erase, a grainy surface, an uneven polish and a stone set slightly off level. */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js';

const box = document.querySelector('[data-ring]');
const canvas = box && box.querySelector('[data-ring-canvas]');

if (canvas) {
  try {
    start();
  } catch (e) {
    box.classList.add('is-fallback');
  }
}

function smooth(a, b, x) {
  const t = Math.min(Math.max((x - a) / (b - a), 0), 1);
  return t * t * (3 - 2 * t);
}
function mix(a, b, t) { return a + (b - a) * t; }

// Deterministic value noise, so the piece has the same quirks on every visit.
function hash(x, y, z) {
  const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
  return h - Math.floor(h);
}
function noise3(x, y, z) {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = x - xi, yf = y - yi, zf = z - zi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf), w = zf * zf * (3 - 2 * zf);
  const c = (dx, dy, dz) => hash(xi + dx, yi + dy, zi + dz);
  const x00 = mix(c(0, 0, 0), c(1, 0, 0), u), x10 = mix(c(0, 1, 0), c(1, 1, 0), u);
  const x01 = mix(c(0, 0, 1), c(1, 0, 1), u), x11 = mix(c(0, 1, 1), c(1, 1, 1), u);
  return mix(mix(x00, x10, v), mix(x01, x11, v), w) * 2 - 1;
}
function fbm(x, y, z) {
  return noise3(x, y, z) * 0.57 + noise3(x * 2.03, y * 2.03, z * 2.03) * 0.29 + noise3(x * 4.1, y * 4.1, z * 4.1) * 0.14;
}

/** A tileable greyscale noise texture: `octaves` of [cells across, cells down, weight]. */
function noiseTexture(w, h, octaves, low, high, seed) {
  const data = new Uint8Array(w * h * 4);
  const grids = octaves.map(([gx, gy], o) => {
    const g = new Float32Array(gx * gy);
    for (let n = 0; n < g.length; n++) g[n] = hash(n, o, seed);
    return g;
  });
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let val = 0;
      octaves.forEach(([gx, gy, weight], o) => {
        const fx = (x / w) * gx, fy = (y / h) * gy;
        const x0 = Math.floor(fx), y0 = Math.floor(fy);
        let tx = fx - x0, ty = fy - y0;
        tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
        const g = grids[o], at = (i, j) => g[((j % gy) * gx) + (i % gx)];
        val += weight * mix(mix(at(x0, y0), at(x0 + 1, y0), tx), mix(at(x0, y0 + 1), at(x0 + 1, y0 + 1), tx), ty);
      });
      const c = Math.round(255 * mix(low, high, val));
      const p = (y * w + x) * 4;
      data[p] = data[p + 1] = data[p + 2] = c;
      data[p + 3] = 255;
    }
  }
  const tex = new THREE.DataTexture(data, w, h);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.magFilter = THREE.LinearFilter;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  return tex;
}

function start() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50);
  camera.position.set(0, 0.9, 7.2);
  camera.lookAt(0, 0, 0);

  // A soft studio "room" for reflections, so the polished silver has something to mirror.
  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new THREE.Scene();
  room.add(new THREE.Mesh(new THREE.BoxGeometry(12, 12, 12), new THREE.MeshBasicMaterial({ color: 0x6a655f, side: THREE.BackSide })));
  [[0, 5.5, 0, 5, 5, 6], [-5.5, 1.5, 1, 2, 5, 4], [5.5, 0.5, -1, 2, 4, 3], [0, 0.5, 5.5, 4, 1.5, 2.4], [0, -5.5, 0, 8, 8, 0.08]].forEach(([x, y, z, w, h, k]) => {
    const panel = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(k, k * 0.98, k * 0.95), side: THREE.DoubleSide }));
    panel.position.set(x, y, z);
    panel.lookAt(0, 0, 0);
    room.add(panel);
  });
  scene.environment = pmrem.fromScene(room, 0.03).texture;

  const key = new THREE.DirectionalLight(0xfff3e6, 1.6);
  key.position.set(3, 4, 5);
  scene.add(key);
  scene.add(new THREE.AmbientLight(0xffffff, 0.25));

  // One surface, parameterised round the band (u) and round its cross-section (v).
  // A tiny hole radius with a fat cross-section reads as a ball; growing the hole and
  // thinning the band opens it into a ring. The first row and column are repeated at the end
  // so the surface texture wraps without a seam.
  const U = 160, V = 96, CU = U + 1, CV = V + 1;
  const positions = new Float32Array(CU * CV * 3);
  const uvs = new Float32Array(CU * CV * 2);
  const index = [];
  for (let i = 0; i < U; i++) {
    for (let j = 0; j < V; j++) {
      const a = i * CV + j, b = (i + 1) * CV + j, c = (i + 1) * CV + j + 1, d = i * CV + j + 1;
      index.push(a, b, d, b, c, d);
    }
  }
  for (let i = 0; i < CU; i++) {
    for (let j = 0; j < CV; j++) {
      uvs[(i * CV + j) * 2] = i / U;
      uvs[(i * CV + j) * 2 + 1] = j / V;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(CU * CV * 3).fill(1), 3));
  geometry.setIndex(index);

  // Surface detail, shared by the band and the collar: a fine grain (bump) and patchy polish
  // (roughness). Slightly uneven colour comes from vertex colours, set from 3D noise so it has
  // no seam or centre point.
  const bumpTex = noiseTexture(512, 128, [[96, 24, 0.45], [192, 48, 0.35], [24, 6, 0.2]], 0, 1, 1);
  const roughTex = noiseTexture(256, 64, [[10, 3, 0.6], [40, 10, 0.4]], 0.62, 1, 2);
  const tint = (x, y, z) => 0.955 + 0.045 * fbm(x * 1.8 + 5, y * 1.8 + 8, z * 1.8 + 1);

  const material = new THREE.MeshPhysicalMaterial({
    color: 0xaaa59d, roughness: 0.9, metalness: 0, envMapIntensity: 0.6, side: THREE.DoubleSide,
    vertexColors: true, bumpMap: bumpTex, bumpScale: 1, roughnessMap: roughTex
  });
  const ring = new THREE.Mesh(geometry, material);

  // The setting: a round brilliant-cut stone held in a raised collar (bezel). The collar shares
  // the band's material, so it goes from clay to silver with the rest of the ring. Its rim is
  // pushed slightly out of round, as if formed by hand.
  const v2 = (x, y) => new THREE.Vector2(x, y);
  const gem = new THREE.Mesh(
    new THREE.LatheGeometry([v2(0, -0.27), v2(0.31, -0.02), v2(0.31, 0.0), v2(0.29, 0.04), v2(0.17, 0.13), v2(0, 0.13)], 16),
    new THREE.MeshPhysicalMaterial({
      color: 0x1846c8, emissive: 0x0a2a92, emissiveIntensity: 0.3, roughness: 0.06, metalness: 0,
      clearcoat: 1, clearcoatRoughness: 0.04, ior: 2.4, specularIntensity: 0.8, envMapIntensity: 1.4,
      iridescence: 0.08, iridescenceIOR: 1.4, flatShading: true
    })
  );
  const bezelGeometry = new THREE.LatheGeometry([v2(0.2, -0.16), v2(0.34, -0.15), v2(0.375, -0.06), v2(0.37, 0.03), v2(0.345, 0.07), v2(0.315, 0.06), v2(0.305, 0.0)], 64);
  const bp = bezelGeometry.attributes.position;
  for (let n = 0; n < bp.count; n++) {
    const x = bp.getX(n), z = bp.getZ(n), a = Math.atan2(z, x);
    const f = 1 + 0.03 * Math.sin(3 * a + 0.8) + 0.018 * Math.sin(5 * a + 2.1);
    bp.setXYZ(n, x * f, bp.getY(n) + 0.012 * Math.sin(2 * a + 0.4) * Math.max(bp.getY(n) + 0.16, 0) * 4, z * f);
  }
  bezelGeometry.computeVertexNormals();
  const bezelTint = new Float32Array(bp.count * 3);
  for (let n = 0; n < bp.count; n++) bezelTint.fill(tint(bp.getX(n) * 3, bp.getY(n) * 3, bp.getZ(n) * 3), n * 3, n * 3 + 3);
  bezelGeometry.setAttribute('color', new THREE.BufferAttribute(bezelTint, 3));
  const bezel = new THREE.Mesh(bezelGeometry, material);
  const setting = new THREE.Group();
  setting.add(bezel, gem);
  setting.rotation.set(0.15, 0, -0.07);

  const group = new THREE.Group();
  group.add(ring, setting);
  group.rotation.x = 0.36;
  group.position.y = -0.14;
  scene.add(group);

  // Each stage keeps some texture and an imperfect finish: sanding softens the clay's dents,
  // and the silver is polished but not mirror-smooth.
  const stages = [
    { color: new THREE.Color('#a39d94'), rough: 0.88, metal: 0, bump: 1.0, dent: 0.03 },   // soft clay
    { color: new THREE.Color('#d6d0c5'), rough: 0.98, metal: 0, bump: 0.85, dent: 0.022 },  // dry clay
    { color: new THREE.Color('#efebe3'), rough: 0.98, metal: 0, bump: 0.75, dent: 0.016 },  // fired, matte white
    { color: new THREE.Color('#e8e8e9'), rough: 0.3, metal: 1, bump: 0.28, dent: 0.011 }   // polished silver
  ];
  const glow = new THREE.Color('#ec6a40');

  let progress = 0, shownE = -1, shownDent = -1, visible = true, raf = 0, spin = 0;

  // Start: a lumpy, slightly squashed ball. The outer half of the band's cross-section lies on
  // the ball's surface and the inner half starts as a line through its middle, so as the shape
  // blends towards the ring the hole opens as a round tunnel rather than a pinch.
  const BALL = 1.05, BALL_DEPTH = 0.9;
  // The finished band is slim underneath and swells up to the stone, with a softly flattened,
  // comfort-fit inside.
  const RING_R = 1.0, RING_T = 0.14, RING_W = 0.3, SWELL_T = 0.1, SWELL_W = 0.14;

  // How the handmade band wanders as you go round it (u): thickness, width, out-of-round and a
  // gentle twist out of plane. Whole-number frequencies so it joins up.
  const thick = (u) => 1 + 0.09 * Math.sin(2 * u + 1.3) + 0.05 * Math.sin(3 * u + 0.4) + 0.03 * Math.sin(5 * u + 2.2);
  const wide = (u) => 1 + 0.1 * Math.sin(2 * u + 2.6) + 0.05 * Math.sin(4 * u + 0.9);
  const round = (u) => 1 + 0.014 * Math.sin(2 * u + 0.7) + 0.009 * Math.sin(3 * u + 2.1);
  const warp = (u) => 0.03 * Math.sin(u + 0.5) + 0.016 * Math.sin(2 * u + 1.9);
  const TOP_U = Math.PI / 2;
  const TOP = RING_R * round(TOP_U) + RING_T * thick(TOP_U) + SWELL_T;

  // Dents and ball lumps are fixed per point on the surface.
  const dents = new Float32Array(CU * CV), lumps = new Float32Array(CU * CV);
  for (let i = 0; i < CU; i++) {
    const u = (i / U) * Math.PI * 2, cu = Math.cos(u), su = Math.sin(u);
    for (let j = 0; j < CV; j++) {
      const v = (j / V) * Math.PI * 2, cv = Math.cos(v), sv = Math.sin(v);
      const r = RING_R + (RING_T + 0.1) * cv;
      dents[i * CV + j] = fbm(r * cu * 2.6 + 7, r * su * 2.6 + 3, RING_W * sv * 2.6 + 11);
      const out = Math.max(cv, 0);
      // Coloured by direction on the ball, which is the same for every point at its centre.
      geometry.attributes.color.array.fill(tint(out * cu * 2, out * su * 2, sv * 2), (i * CV + j) * 3, (i * CV + j) * 3 + 3);
      lumps[i * CV + j] = fbm(out * cu * 1.5 + 2, out * su * 1.5 + 5, sv * 1.5 + 9);
    }
  }

  function shapeRing(e, dentAmp) {
    const lump = 0.085 * (1 - smooth(0, 0.7, e));   // hand-rolled lumps while it's still a ball
    let k = 0;
    for (let i = 0; i < CU; i++) {
      const u = (i / U) * Math.PI * 2;
      const cu = Math.cos(u), su = Math.sin(u);
      const th = thick(u), wd = wide(u), rr = RING_R * round(u), wz = warp(u);
      for (let j = 0; j < CV; j++) {
        const v = (j / V) * Math.PI * 2;
        const cv = Math.cos(v), sv = Math.sin(v);
        const out = Math.max(cv, 0);
        // The lumps fade out towards the centre point, so the ball stays seamless there.
        const n = 1 + lump * out * lumps[i * CV + j];
        const bx = BALL * out * cu * n, by = BALL * out * su * n, bz = BALL_DEPTH * sv * (cv > 0 ? n : 1);
        const top = Math.pow(Math.max(su, 0), 3);
        const r = rr + (RING_T * th + SWELL_T * top) * (cv < 0 ? cv * 0.72 : cv);
        const dn = dentAmp * dents[i * CV + j] * (1 - 0.7 * top);
        positions[k++] = mix(bx, r * cu + dn * cv * cu, e);
        positions[k++] = mix(by, r * su + dn * cv * su, e);
        positions[k++] = mix(bz, (RING_W * wd + SWELL_W * top) * sv + wz + dn * sv, e);
      }
    }
    geometry.attributes.position.needsUpdate = true;
    geometry.computeVertexNormals();
    const nrm = geometry.attributes.normal.array;
    // While it's (mostly) a ball, light it partly from the ball's true shape so the centre stays
    // seamless; hand over to the mesh's own shading as the ring forms. Towards the edge most of
    // the mesh shading is kept so the lumps catch the light.
    const w = 1 - smooth(0, 0.4, e);
    if (w > 0) {
      let o = 0;
      for (let i = 0; i < CU; i++) {
        const u = (i / U) * Math.PI * 2;
        const cu = Math.cos(u), su = Math.sin(u);
        for (let j = 0; j < CV; j++, o += 3) {
          const v = (j / V) * Math.PI * 2;
          const cv = Math.cos(v), sv = Math.sin(v);
          const out = Math.max(cv, 0);
          const ax = (out * cu) / BALL, ay = (out * su) / BALL, az = sv / BALL_DEPTH;
          const al = Math.hypot(ax, ay, az) || 1;
          const k2 = w * (1 - 0.8 * out);
          const x = mix(nrm[o], ax / al, k2), y = mix(nrm[o + 1], ay / al, k2), z = mix(nrm[o + 2], az / al, k2);
          const l = Math.hypot(x, y, z) || 1;
          nrm[o] = x / l; nrm[o + 1] = y / l; nrm[o + 2] = z / l;
        }
      }
    }
    // The repeated seam row and column get the average of both sides, so no seam shows.
    const join = (p, q) => {
      for (let c = 0; c < 3; c++) {
        const avg = (nrm[p * 3 + c] + nrm[q * 3 + c]) / 2;
        nrm[p * 3 + c] = nrm[q * 3 + c] = avg;
      }
    };
    for (let j = 0; j < CV; j++) join(j, U * CV + j);
    for (let i = 0; i < CU; i++) join(i * CV, i * CV + V);
    geometry.attributes.normal.needsUpdate = true;
    setting.position.set(0, mix(BALL, TOP, e) + 0.02, warp(TOP_U) * e);
  }

  // The surface grain follows the band's own coordinates, which bunch up at the front of the
  // ball, so it fades in as the ring forms; the ball's texture comes from its lumps instead.
  function paint(s, e) {
    const n = Math.min(Math.floor(s), 2), t = s - n;
    const a = stages[n], b = stages[n + 1];
    material.color.copy(a.color).lerp(b.color, t);
    material.roughness = mix(a.rough, b.rough, t);
    material.metalness = mix(a.metal, b.metal, t);
    material.bumpScale = mix(a.bump, b.bump, t) * smooth(0.35, 0.9, e);
    // The salmon glow of firing peaks on the "fired" stage.
    const g = Math.max(0, 1 - Math.abs(s - 2) / 0.6);
    material.emissive.copy(glow);
    material.emissiveIntensity = g * 1.7;
    material.envMapIntensity = mix(0.6, 1.5, Math.max(0, s - 2));
    return mix(a.dent, b.dent, t);
  }

  function render() {
    const e = smooth(0.06, 0.34, progress);
    const dent = paint(Math.min(progress * 3, 3), e);
    if (Math.abs(e - shownE) > 0.0005 || Math.abs(dent - shownDent) > 0.0002) {
      shapeRing(e, dent);
      shownE = e;
      shownDent = dent;
    }
    const pop = smooth(0.24, 0.36, progress);
    setting.visible = pop > 0.01;
    setting.scale.setScalar(1.12 * pop);
    gem.rotation.y = spin * 1.6;
    group.rotation.y = Math.sin(spin) * 0.4 + progress * 0.5;
    renderer.render(scene, camera);
  }

  function resize() {
    const w = box.clientWidth, h = box.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    render();
  }

  function loop() {
    raf = 0;
    if (!visible || reduceMotion) return;
    spin += 0.006;
    render();
    raf = requestAnimationFrame(loop);
  }
  function kick() { if (!raf && visible && !reduceMotion) raf = requestAnimationFrame(loop); }

  window.addEventListener('atl-journey:progress', (ev) => {
    progress = ev.detail.progress;
    if (reduceMotion || !raf) render();
  });
  window.addEventListener('atl-guide:chapter', () => requestAnimationFrame(resize));
  new ResizeObserver(resize).observe(box);
  new IntersectionObserver((entries) => {
    visible = entries[0].isIntersecting;
    if (visible) kick();
  }).observe(box);

  box.classList.add('is-ready');
  resize();
  kick();
}
